import { useEffect, useState } from "react";

import { safeLocalStorage } from "utils/safeLocalStorage";
import { executeVSCodeCommand } from "utils/vscode";

import { useAnalytics } from "./useAnalytics";

export type ChangelogStatus = (
  | {
      status: "available";
      date: number;
    }
  | {
      status: "unavailable";
    }
  | {
      status: "loading";
    }
) & {
  open: () => void;
};

export const useChangelogAvailable = () => {
  const track = useAnalytics();
  const open = () =>
    executeVSCodeCommand("codesandbox.openCsbTab", {
      type: "CHANGELOG",
    });
  const [changelogStatus, setChangelogStatus] = useState<ChangelogStatus>({
    status: "loading",
    open,
  });

  useEffect(() => {
    if (changelogStatus.status !== "loading") {
      return;
    }

    const abortController = new AbortController();
    fetch("https://codesandbox.io/changelog/version", {
      signal: abortController.signal,
    })
      .then((x) => x.json())
      .then((res) => res.latestDate as number)
      .then((latestVersion) => {
        if (!latestVersion) {
          return;
        }

        const latestUpdate = Number(safeLocalStorage.get("CSB_LAST_VERSION"));
        if (latestUpdate < latestVersion) {
          track("show_changelog_note");
          setChangelogStatus({
            status: "available",
            date: latestVersion,
            open: () => {
              safeLocalStorage.set(
                "CSB_LAST_VERSION",
                latestVersion.toString(),
              );
              executeVSCodeCommand("codesandbox.openCsbTab", {
                type: "CHANGELOG",
              });
              setChangelogStatus({ status: "unavailable", open });
              track("open_changelog");
            },
          });
        } else {
          setChangelogStatus({ status: "unavailable", open });
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("Failed to get latest changelog", e);
      });

    return () => {
      abortController.abort();
    };
  }, [changelogStatus]);

  return changelogStatus;
};
